(ns app.front.view.case-note.list
  (:require [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil]
            [app.front.view.case-note.subs :as cn-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]))

(defn- save-button
  [{:keys [event disabled?]
    :or   {disabled? false}}]
  (let [tr    (<sub [::cn-subs/tr])
        title (tr [:save])]
    (cc/button
      {:kind     :primary
       :size     :small
       :disabled disabled?
       :on-click #(>evt event)}
      title)))

(defn- cancel-button
  [{:keys [event]}]
  (let [tr    (<sub [::cn-subs/tr])
        title (tr [:cancel])]
    (cc/button
      {:kind     :secondary
       :size     :small
       :on-click #(>evt event)}
      title)))

(defn- edit-button
  [id]
  (let [tr      (<sub [::cn-subs/tr])
        title   (tr [:edit])
        content (<sub [::cn-subs/content id])]
    (cc/button
      {:kind     :secondary
       :size     :small
       :on-click #(>evt [::cn-subs/edit-button-clicked id content])}
      title)))

(defn- delete-button
  [{:keys [id note-phase-id]}]
  (let [tr    (<sub [::cn-subs/tr])
        title (tr [:delete])]
    (cc/button
      {:kind     :secondary
       :size     :small
       :on-click #(>evt [::cn-subs/delete-button-clicked note-phase-id id])}
      title)))

(defn- delete-modal
  []
  (let [tr                         (<sub [::cn-subs/tr])
        {:keys [id note-phase-id]} (<sub [::cn-subs/delete-modal-data])]
    [ui/confirm-modal
     {:open?        (<sub [::cn-subs/show-delete-modal?])
      :tr           tr
      :id           "delete-case-note-modal"
      :modal-title  (tr [:case-note/delete-title])
      :on-close     #(>evt [::cn-subs/delete-cancelled])
      :on-yes-click #(>evt [::cn-subs/delete-confirmed note-phase-id id])}
     (tr [:case-note/delete-body])]))

(defn- add-note-view
  [current-phase-id]
  (let [tr (<sub [::cn-subs/tr])]
    [:div.space-y-2
     [ui-rt/editor-legacy {:id                 "new-note"
                           :aria-label         (tr [:case-note/content])
                           :initial-value      ""
                           :authority-group-id (<sub [::ws-subs/authority-group-id])
                           :on-change          #(>evt [::cn-subs/note-content-changed %])}]
     [:div.flex.space-x-2.justify-end
      [cancel-button {:event [::cn-subs/clear-changes]}]
      [save-button {:event     [::cn-subs/save-new-note current-phase-id]
                    :disabled? (not (<sub [::cn-subs/has-local-content?]))}]]]))


(defn- note-header
  [id]
  (let [tr (<sub [::cn-subs/tr])]
    [:div.text-ui-night-sky-100
     [:span (tr [:case-note/last-modified]
                [(<sub [::cn-subs/last-modified-timestamp id])
                 (<sub [::cn-subs/last-modified-by id])])]
     (cond
       (<sub [::cn-subs/edited? id])
       [:span (str " (" (tr [:case-note/edited]) ")")]

       (<sub [::cn-subs/copied? id])
       [:span (str " (" (tr [:case-note/copied-from-previous-phase-workspace]) ")")])]))

(defn show-hide-controls
  [id]
  ;; TODO: "User click/tap outside the note or by selecting "Show less" to close open fold"
  (when (and (<sub [::cn-subs/truncate-content? id])
             (not (<sub [::cn-subs/editing-note? id])))
    (let [tr (<sub [::cn-subs/tr])]
      (if (<sub [::cn-subs/note-expanded? id])
        (cc/link
          {:on-click #(>evt [::cn-subs/show-less-clicked id])}
          [:span (tr [:case-note/show-less])])
        (cc/link
          {:on-click #(>evt [::cn-subs/show-more-clicked id])}
          [:span (tr [:case-note/show-more])])))))

(defn- note-content
  [id]
  (let [content  (<sub [::cn-subs/visible-content id])
        editing? (<sub [::cn-subs/editing-note? id])
        tr       (<sub [::cn-subs/tr])]
    [:<>
     ^{:key (<sub [::cn-subs/editor-hash id])}
     [ui-rt/editor-legacy (merge
                            {:id                 (str "case-note-" id)
                             :aria-label         (tr [:case-note/content])
                             :authority-group-id (<sub [::ws-subs/authority-group-id])
                             :initial-value      content
                             :max-height         150}
                            (when editing?
                              {:on-change #(>evt [::cn-subs/note-content-changed %])}))]
     [show-hide-controls id]]))

(defn- note-controls
  [{:keys [id note-phase-id]}]
  (let [has-focus? (<sub [::cn-subs/note-focused? id])
        editing?   (<sub [::cn-subs/editing-note? id])]
    [:div.h-10
     (when has-focus?
       [:div.flex.space-x-2.justify-end
        (if editing?
          [:<>
           [cancel-button {:event [::cn-subs/cancel-editing-note]}]
           [save-button {:event     [::cn-subs/save-edited-changes note-phase-id id]
                         :disabled? (not (<sub [::cn-subs/edits-made? id]))}]]
          [:<>
           [edit-button id]
           [delete-button (util/mmap id note-phase-id)]])])]))

(defn- focusable
  [{:keys [on-focus on-unfocus]} & children]
  (into [:div
         {:tab-index     0
          :on-focus      on-focus
          :on-blur       on-unfocus
          :on-mouse-over on-focus
          :on-mouse-out  on-unfocus}]
        children))

(defn- note
  [{:keys [id note-phase-id]}]
  (let [has-focus? (<sub [::cn-subs/note-focused? id])]
    ^{:key id}
    [focusable
     {:on-focus   #(>evt [::cn-subs/note-focused id])
      :on-unfocus #(>evt [::cn-subs/note-unfocused])}
     [:div.rounded-lg.py-2.px-2.space-y-2
      {:class (if has-focus? "bg-ui-blue-20" "bg-ui-light-gray")}
      [note-header id]
      [note-content id]
      (when (<sub [::process/signal-permitted? note-phase-id [:process.signal/edit-case-note]])
        [note-controls (util/mmap id note-phase-id)])]]))

(defn- list-notes
  []
  [:div.flex.flex-col.space-y-2
   (futil/for-frag [{:keys [id note-phase-id]} (<sub [::cn-subs/case-note-ids-sorted])]
     [note (util/mmap id note-phase-id)])])

(defn view
  [current-phase-id]
  [:div.flex.flex-col.space-y-2
   [delete-modal]
   (when (<sub [::cn-subs/creating-new-note?])
     [add-note-view current-phase-id])
   (when (<sub [::cn-subs/has-case-notes?])
     [list-notes])])
